<template>
  <div
    class="quantity-selector-container"
    :class="{
      'quantity-selector-large': isLarge,
      'quantity-selector-flexible': isFlexible,
    }"
  >
    <button
      ref="decrementButton"
      aria-label="Decrease quantity"
      class="quantity-button-minus"
      :disabled="isDecrementDisabled"
      :tabindex="isDecrementDisabled ? -1 : 0"
      @click="handleDecrement"
    />
    <input
      :id="id"
      type="number"
      title="Quantity"
      class="quantity-selector-input"
      :aria-describedby="ariaDescribedbyId"
      name="quantity"
      :value="quantity"
      disabled=""
      inputmode=""
      :aria-disabled="disabled.toString()"
      :readonly="disabled"
    >
    <label
      class="qty-label"
      :for="id"
    >Qty</label>
    <button
      ref="incrementButton"
      aria-label="Increase quantity"
      class="quantity-button-plus"
      :disabled="isIncrementDisabled"
      :tabindex="isIncrementDisabled ? -1 : 0"
      @click="handleIncrement"
    />
    <div
      :id="ariaDescribedbyId"
      class="show-for-sr"
      role="status"
      aria-live="polite"
    >
      {{ ariaLiveDescription }}
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  ref,
  onMounted,
  computed,
  watch,
  nextTick,
} from 'vue';
import { useDebounceFn } from '@vueuse/core';
import generateRandomId from '../../utils/randomNumber';

export default defineComponent({
  name: 'QuantitySelector',
  props: {
    id: {
      type: [String, Number],
      default: () => generateRandomId(),
    },
    modelValue: {
      type: Number,
      default: 1,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 1,
    },
    size: {
      type: String,
      default: 'small',
      validator: (val) => ['small', 'large'].includes(val),
    },
    isFlexible: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const quantity = ref(parseInt(props.modelValue, 10));
    const ariaDescribedbyId = generateRandomId();
    const ariaLiveDescription = ref('');
    const onChange = useDebounceFn((value) => {
      emit('change', value);
    }, 500);
    const isLarge = computed(() => props.size === 'large');
    const decrementButton = ref(null);
    const incrementButton = ref(null);
    const isDecrementDisabled = computed(
      () => quantity.value === props.min || props.disabled,
    );
    const isIncrementDisabled = computed(
      () => quantity.value === props.max || props.disabled,
    );
    function onQuantityChange(value) {
      emit('update:modelValue', value);
      onChange(value);
    }
    function handleDecrement() {
      quantity.value -= 1;
      ariaLiveDescription.value = `Quantity is decremented to ${quantity.value}`;
      onQuantityChange(quantity.value);
      nextTick(() => {
        if (isDecrementDisabled.value) {
          incrementButton.value.focus();
        }
      });
    }
    function handleIncrement() {
      quantity.value += 1;
      ariaLiveDescription.value = `Quantity is incremented to ${quantity.value}`;
      onQuantityChange(quantity.value);
      nextTick(() => {
        if (isIncrementDisabled.value) {
          decrementButton.value.focus();
        }
      });
    }
    function checkQuantityValue() {
      if (quantity.value >= props.max) {
        quantity.value = props.max;
      } else if (quantity.value < props.min) {
        quantity.value = props.min;
      }
    }
    watch(
      () => props.modelValue,
      (newValue) => {
        quantity.value = newValue;
        checkQuantityValue();
      },
    );
    onMounted(() => {
      checkQuantityValue();
      onQuantityChange(quantity.value);
    });
    return {
      quantity,
      onQuantityChange,
      handleDecrement,
      handleIncrement,
      isLarge,
      ariaLiveDescription,
      ariaDescribedbyId,
      decrementButton,
      incrementButton,
      isDecrementDisabled,
      isIncrementDisabled,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "~@core/csg/src/scss/forms/forms.scss";
@import "~@core/csg/src/scss/selectors/selectors.scss";
@include csg-forms;
@include csg-selectors;
</style>
